import { doc, onSnapshot, getFirestore } from 'firebase/firestore';

import { getFirebaseWebApp } from 'src/auth/store/firebase/lib';
import {
  getSessionGeneration,
  registerSessionCleanup,
  isSessionGenerationCurrent,
} from 'src/auth/session-lifecycle';

export type BusinessDocumentSnapshot = {
  data: Record<string, unknown> | null;
  fromCache: boolean;
  exists: boolean;
};
type Subscriber = {
  next: (snapshot: BusinessDocumentSnapshot) => void;
  error?: (error: Error) => void;
};
type Subscription = {
  generation: number;
  subscribers: Set<Subscriber>;
  last?: BusinessDocumentSnapshot;
  stop: () => void;
};
const subscriptions = new Map<string, Subscription>();

function reportConsumerError(listener: Subscriber, failure: Error): void {
  try {
    listener.error?.(failure);
  } catch {
    console.error('[BusinessDocumentSnapshots] Consumer error handler failed');
  }
}

function notifyConsumer(listener: Subscriber, snapshot: BusinessDocumentSnapshot): void {
  try {
    listener.next(snapshot);
  } catch (failure) {
    reportConsumerError(
      listener,
      failure instanceof Error ? failure : new Error('Business document handler failed')
    );
  }
}

registerSessionCleanup(() => {
  subscriptions.forEach((subscription) => subscription.stop());
  subscriptions.clear();
});

/** One transport listener shared by boot, configuration hooks, and runtime sync. */
export function subscribeBusinessDocumentSnapshots(
  businessId: string,
  next: Subscriber['next'],
  error?: Subscriber['error']
): () => void {
  if (!businessId) return () => {};
  const subscriber = { next, error };
  let subscription = subscriptions.get(businessId);
  if (!subscription) {
    subscription = { generation: getSessionGeneration(), subscribers: new Set(), stop: () => {} };
    subscriptions.set(businessId, subscription);
    const current = subscription;
    current.stop = onSnapshot(
      doc(getFirestore(getFirebaseWebApp()), 'business', businessId),
      { includeMetadataChanges: true },
      (snapshot) => {
        if (
          subscriptions.get(businessId) !== current ||
          !isSessionGenerationCurrent(current.generation)
        )
          return;
        const payload: BusinessDocumentSnapshot = {
          data: snapshot.exists() ? snapshot.data() : null,
          exists: snapshot.exists(),
          fromCache: snapshot.metadata.fromCache,
        };
        current.last = payload;
        // Isolate both data and error handlers so a malformed configuration
        // cannot prevent another consumer's revocation or recovery handling.
        current.subscribers.forEach((listener) => notifyConsumer(listener, payload));
      },
      (failure) => {
        if (
          subscriptions.get(businessId) !== current ||
          !isSessionGenerationCurrent(current.generation)
        )
          return;
        current.subscribers.forEach((listener) => reportConsumerError(listener, failure));
      }
    );
  }
  const current = subscription;
  current.subscribers.add(subscriber);
  if (current.last) notifyConsumer(subscriber, current.last);
  return () => {
    current.subscribers.delete(subscriber);
    if (!current.subscribers.size) {
      current.stop();
      if (subscriptions.get(businessId) === current) subscriptions.delete(businessId);
    }
  };
}
